import { useState, useCallback } from 'react'
import { useAccount, usePublicClient, useWalletClient } from 'wagmi'
import { useToast } from '@/providers/ToastProvider'

/// Thin wrapper around viem's simulate + write + waitForTransactionReceipt
/// pattern. Returns `{ run, pending, txHash, error }` - `run` takes the
/// simulate args for `writeContract`.
///
/// Usage:
///   const tx = useContractTx()
///   await tx.run({ address, abi, functionName, args, value }, { successMsg: '...' })
export function useContractTx() {
  const publicClient = usePublicClient()
  const { address: account, isConnected } = useAccount()
  // `enabled: isConnected` (not just gating on `account` after the fact) matters here: wagmi
  // resolves a wallet client by calling the active connector's own `getProvider()` internally,
  // and `isConnected` only flips true once that connector has genuinely finished its (async)
  // reconnect handshake. Without this gate, useWalletClient fires immediately on mount - every
  // page with a wallet-action form calls useContractTx unconditionally, so this ran on every
  // load - and can call getProvider() on a connector wagmi has only optimistically restored from
  // storage but not yet finished reconnecting, throwing "connector.getProvider is not a
  // function" (a known wagmi race - https://github.com/wevm/wagmi/issues/3589). Same guard
  // useEip1193Provider.js already applies to its own direct connector.getProvider() call.
  const { data: walletClient } = useWalletClient({ query: { enabled: isConnected } })
  const toast = useToast()

  const [pending, setPending] = useState(false)
  const [txHash, setTxHash] = useState(null)
  const [error, setError] = useState(null)

  const run = useCallback(async (writeArgs, opts = {}) => {
    if (!walletClient || !account) {
      toast.push('Connect a wallet first', 'error')
      return null
    }
    setPending(true)
    setError(null)
    setTxHash(null)
    try {
      // Simulate first so we get the friendlier revert reason in the
      // toast instead of a raw rpc error shape.
      const sim = await publicClient.simulateContract({
        account,
        ...writeArgs,
      })
      const hash = await walletClient.writeContract(sim.request)
      setTxHash(hash)
      if (opts.pendingMsg) toast.push(opts.pendingMsg, 'info')
      const receipt = await publicClient.waitForTransactionReceipt({ hash })
      if (receipt.status === 'success') {
        toast.push(opts.successMsg || 'Transaction confirmed', 'success')
      } else {
        toast.push('Transaction reverted', 'error')
      }
      return receipt
    } catch (e) {
      const msg = e?.shortMessage || e?.message || 'Transaction failed'
      setError(msg)
      toast.push(msg.slice(0, 180), 'error', 6000)
      return null
    } finally {
      setPending(false)
    }
  }, [publicClient, walletClient, account, toast])

  return { run, pending, txHash, error }
}
